import { Fragment, useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Power, TicketPercent, Trash2, X } from "lucide-react";
import { api, fmt, fmtDate, apiError } from "@/lib/api";

const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const EMPTY_FORM = { code: "", type: "percent", value: "", min_order: "", max_discount: "", usage_limit: "", expires_on: "", active: true, stack_with_prime: false };

const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
const isExhausted = (c) => c.usage_limit && (c.used_count || 0) >= c.usage_limit;

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // coupon row or null for new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmCode, setConfirmCode] = useState(null);
  const [busyCode, setBusyCode] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/coupons")
      .then(({ data }) => setCoupons(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load coupons")));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setModalOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, type: c.type, value: String(c.value),
      min_order: String(c.min_order ?? 0), max_discount: c.max_discount ? String(c.max_discount) : "",
      usage_limit: String(c.usage_limit ?? 1000), expires_on: (c.expires_at || "").slice(0, 10), active: !!c.active,
      stack_with_prime: !!c.stack_with_prime,
    });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const value = parseFloat(form.value);
    if (!editing && form.code.trim().length < 3) return toast.error("Code needs at least 3 characters");
    if (!value || value <= 0) return toast.error("Discount value must be greater than zero");
    if (form.type === "percent" && value > 100) return toast.error("Percent discount can't exceed 100%");
    if (!form.expires_on) return toast.error("Pick an expiry date");
    const payload = {
      type: form.type,
      value,
      min_order: parseFloat(form.min_order) || 0,
      max_discount: form.type === "percent" && form.max_discount !== "" ? parseFloat(form.max_discount) : null,
      usage_limit: parseInt(form.usage_limit) || 1000,
      expires_at: `${form.expires_on}T23:59:59+00:00`,
      active: form.active,
      stack_with_prime: form.stack_with_prime,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/coupons/${editing.code}`, payload);
        toast.success(`${editing.code} updated`);
      } else {
        await api.post("/admin/coupons", { ...payload, code: form.code.trim().toUpperCase() });
        toast.success(`Coupon ${form.code.trim().toUpperCase()} created`);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Couldn't save coupon"));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    setBusyCode(c.code);
    try {
      await api.put(`/admin/coupons/${c.code}`, { active: !c.active });
      toast.success(`${c.code} ${c.active ? "deactivated" : "activated"}`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Couldn't update coupon"));
    } finally {
      setBusyCode(null);
    }
  };

  const remove = async (c) => {
    if (confirmCode !== c.code) return setConfirmCode(c.code);
    setConfirmCode(null);
    setBusyCode(c.code);
    try {
      await api.delete(`/admin/coupons/${c.code}`);
      toast.success(`${c.code} deleted`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Couldn't delete coupon"));
    } finally {
      setBusyCode(null);
    }
  };

  if (!coupons) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  }

  const activeCount = coupons.filter((c) => c.active && !isExpired(c)).length;
  const redemptions = coupons.reduce((s, c) => s + (c.used_count || 0), 0);

  return (
    <div data-testid="admin-coupons">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Coupons</h2>
          <p className="mt-1 text-sm text-neutral-500" data-testid="coupons-stats">
            {coupons.length} coupons · {activeCount} live · {redemptions.toLocaleString("en-IN")} redemptions
          </p>
        </div>
        <button data-testid="coupon-new-btn" onClick={openNew}
          className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New Coupon
        </button>
      </div>

      {coupons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center" data-testid="coupons-empty">
          <TicketPercent className="mx-auto h-10 w-10 text-neutral-600" />
          <p className="mt-3 text-sm text-neutral-500">No coupons yet — create your first one.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]" data-testid="coupons-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                <th className="px-5 py-3.5">Code</th>
                <th className="px-5 py-3.5">Discount</th>
                <th className="px-5 py-3.5">Min Order</th>
                <th className="px-5 py-3.5">Usage</th>
                <th className="px-5 py-3.5">Expires</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const expired = isExpired(c);
                const exhausted = isExhausted(c);
                const pct = c.usage_limit ? Math.min(100, ((c.used_count || 0) / c.usage_limit) * 100) : 0;
                return (
                  <tr key={c.code} className="border-b border-white/[0.05] last:border-0" data-testid={`coupon-row-${c.code}`}>
                    <td className="px-5 py-4">
                      <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 font-mono text-xs font-bold text-amber-300">{c.code}</span>
                    </td>
                    <td className="px-5 py-4" data-testid={`coupon-discount-${c.code}`}>
                      <span className="font-bold text-white">{c.type === "percent" ? `${c.value}% off` : `${fmt(c.value)} off`}</span>
                      {c.type === "percent" && c.max_discount && (
                        <span className="block text-xs text-neutral-500">up to {fmt(c.max_discount)}</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">{c.min_order ? fmt(c.min_order) : "—"}</td>
                    <td className="px-5 py-4" data-testid={`coupon-usage-${c.code}`}>
                      <span className="text-xs text-neutral-300">{(c.used_count || 0).toLocaleString("en-IN")} / {(c.usage_limit || 0).toLocaleString("en-IN")}</span>
                      <div className="mt-1.5 h-1.5 w-24 overflow-hidden rounded-full bg-white/[0.07]">
                        <div className={`h-full rounded-full ${exhausted ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className="px-5 py-4" data-testid={`coupon-expiry-${c.code}`}>
                      <span className="text-neutral-300">{fmtDate(c.expires_at)}</span>
                      {expired && <span className="ml-2 rounded-full border border-red-500/40 px-2 py-0.5 text-[10px] font-bold uppercase text-red-400">Expired</span>}
                      {!expired && exhausted && <span className="ml-2 rounded-full border border-orange-500/40 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-400">Used up</span>}
                    </td>
                    <td className="px-5 py-4">
                      <span data-testid={`coupon-status-${c.code}`}
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${c.active ? "border-emerald-500/40 text-emerald-400" : "border-neutral-500/40 text-neutral-500"}`}>
                        {c.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`coupon-edit-${c.code}`} onClick={() => openEdit(c)} aria-label={`Edit ${c.code}`}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-amber-500/50 hover:text-amber-400">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} disabled={busyCode === c.code}
                          aria-label={`${c.active ? "Deactivate" : "Activate"} ${c.code}`}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:opacity-50 ${c.active ? "border-emerald-500/30 text-emerald-400 hover:border-red-500/50 hover:text-red-400" : "border-white/10 text-neutral-500 hover:border-emerald-500/50 hover:text-emerald-400"}`}>
                          <Power className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={confirmCode === c.code ? `coupon-delete-confirm-${c.code}` : `coupon-delete-${c.code}`}
                          onClick={() => remove(c)} onBlur={() => setConfirmCode(null)} disabled={busyCode === c.code}
                          aria-label={`Delete ${c.code}`}
                          className={`flex h-8 items-center justify-center gap-1 rounded-lg border px-2 transition-colors disabled:opacity-50 ${confirmCode === c.code ? "border-red-500 bg-red-500/15 text-red-400" : "border-white/10 text-neutral-400 hover:border-red-500/50 hover:text-red-400"}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                          {confirmCode === c.code && <span className="text-[10px] font-bold uppercase">Sure?</span>}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {modalOpen && (
          <Fragment key="coupon-modal">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setModalOpen(false)} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              className="fixed inset-x-4 top-[5vh] z-[85] mx-auto max-w-md rounded-2xl border border-white/10 bg-[#F1EDE3] p-6" data-testid="coupon-modal">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="font-display flex items-center gap-2 text-lg font-semibold tracking-tight">
                  <TicketPercent className="h-5 w-5 text-amber-500" /> {editing ? `Edit ${editing.code}` : "New Coupon"}
                </h3>
                <button onClick={() => setModalOpen(false)} data-testid="coupon-cancel-btn" className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" aria-label="Close"><X className="h-4 w-4" /></button>
              </div>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Coupon code</label>
                  <input data-testid="coupon-form-code" required value={form.code} disabled={!!editing}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32) }))}
                    placeholder="DIWALI25" className={`${inputCls} font-mono disabled:opacity-50`} />
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Discount type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[["percent", "Percent %"], ["fixed", "Flat ₹"]].map(([t, label]) => (
                      <button key={t} type="button" data-testid={`coupon-form-type-${t}`}
                        onClick={() => setForm((f) => ({ ...f, type: t }))}
                        className={`rounded-xl border py-2.5 text-sm font-bold transition-colors ${form.type === t ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-white/10 text-neutral-400 hover:border-white/25"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                      {form.type === "percent" ? "Percent off" : "Amount off ₹"}
                    </label>
                    <input data-testid="coupon-form-value" required type="number" min="0.01" step="0.01" value={form.value}
                      onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                      placeholder={form.type === "percent" ? "10" : "100"} className={inputCls} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Min order ₹</label>
                    <input data-testid="coupon-form-min-order" type="number" min="0" step="1" value={form.min_order}
                      onChange={(e) => setForm((f) => ({ ...f, min_order: e.target.value }))} placeholder="0" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Max discount ₹</label>
                    <input data-testid="coupon-form-max-discount" type="number" min="0" step="1" value={form.max_discount}
                      disabled={form.type !== "percent"}
                      onChange={(e) => setForm((f) => ({ ...f, max_discount: e.target.value }))}
                      placeholder={form.type === "percent" ? "Optional cap" : "Percent only"} className={`${inputCls} disabled:opacity-50`} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Usage limit</label>
                    <input data-testid="coupon-form-usage-limit" type="number" min="1" step="1" value={form.usage_limit}
                      onChange={(e) => setForm((f) => ({ ...f, usage_limit: e.target.value }))} placeholder="1000" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Valid until</label>
                  <input data-testid="coupon-form-expires" required type="date" value={form.expires_on}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setForm((f) => ({ ...f, expires_on: e.target.value }))}
                    className={`${inputCls} [color-scheme:dark]`} />
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <span className="text-sm font-semibold text-white">Active (customers can use it)</span>
                  <button type="button" role="switch" aria-checked={form.active} data-testid="coupon-form-active"
                    onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.active ? "bg-emerald-500" : "bg-white/10"}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.active ? "left-6" : "left-1"}`} />
                  </button>
                </label>
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <span className="text-sm font-semibold text-white">Stacks with PRIME member pricing
                    <span className="block text-[11px] font-medium text-neutral-500">Off = members can't use this coupon on PRIME-priced carts</span>
                  </span>
                  <button type="button" role="switch" aria-checked={form.stack_with_prime} data-testid="coupon-form-stack-prime"
                    onClick={() => setForm((f) => ({ ...f, stack_with_prime: !f.stack_with_prime }))}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.stack_with_prime ? "bg-amber-500" : "bg-white/10"}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.stack_with_prime ? "left-6" : "left-1"}`} />
                  </button>
                </label>
                <button data-testid="coupon-save-btn" type="submit" disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing ? "Save Changes" : "Create Coupon"}
                </button>
              </form>
            </motion.div>
          </Fragment>
        )}
      </AnimatePresence>
    </div>
  );
}
